import crossbarCenterDayImg from '@assets/web-v3-images/progress-img.png';
import crossbarLeftDayImg from '@assets/web-v3-images/crossbar-day-left.png';
import crossbarRightDayImg from '@assets/web-v3-images/crossbar-day-right.png';
import ellipseImg from '@assets/web-v3-images/Ellipse30.png';
import image487Img from '@assets/web-v3-images/image487.png';
import lineCrossbarImg from '@assets/web-v3-images/line-crossbar.png';

import img100Active from '@assets/web-v3-images/img-100-active.png';
import img100 from '@assets/web-v3-images/img-100.png';
import img1000Active from '@assets/web-v3-images/img-1000-active.png';
import img1000 from '@assets/web-v3-images/img-1000.png';
import img500Active from '@assets/web-v3-images/img-500-active.png';
import img500 from '@assets/web-v3-images/img-500.png';
import img5000Active from '@assets/web-v3-images/img-5000-active.png';
import img5000 from '@assets/web-v3-images/img-5000.png';
import piece from '@assets/web-v3-images/piece.png';
import { useEffect, useRef, useState } from 'react';

export default function ProgressVault({ setValue, value, setSelectedState, data }) {
	const hasUserInteracted = useRef(false);
	const days = [
		{
			id: 0,
			type: "piece",
			value: data?.piece_balance,
			active: true,
		},
		...(data?.vaults ?? [])?.map((item) => ({
			id: item.vault_order,
			type: "vault",
			value: item.piece_required,
			active: item.state === 'Opened',
			reward: item.reward_lp,
			state: item.state,
		})),
	];

	const imageList = [
		{ active: piece, inactive: piece },
		{ active: img100Active, inactive: img100 },
		{ active: img500Active, inactive: img500 },
		{ active: img5000Active, inactive: img5000 },
		{ active: img1000Active, inactive: img1000 },
	];

	const imageMap = { // map để show ảnh tương ứng với  piece_required
		[data?.piece_balance]: imageList[0],
		...Object.fromEntries(
			(data.vaults ?? [])?.map((item, index) => [
				item.piece_required,
				imageList[index + 1],
			])
		),
	};

	// Các mốc piece_required, mốc đầu tiên (item "piece") coi như 0
	const thresholds = [0, ...(data?.vaults ?? []).map((v) => v.piece_required)];

	// Tìm vị trí (số thực) của piece_balance trên trục thresholds
	const getProgressPosition = (balance) => {
		if (!thresholds.length) return 0;
		if (balance <= thresholds[0]) return 0;
		for (let i = 0; i < thresholds.length - 1; i++) {
			const start = thresholds[i];
			const end = thresholds[i + 1];
			if (balance >= start && balance <= end) {
				return end === start ? i : i + (balance - start) / (end - start);
			}
		}
		// Vượt mốc cuối cùng -> giữ ở vị trí cuối (100%)
		return thresholds.length - 1;
	};

	const progressPosition = getProgressPosition(data?.piece_balance ?? 0);
	const equivalentActiveCount = progressPosition + 1; // +1 vì item "piece" luôn coi là đã qua
	const progressWidth =
		(data?.piece_balance ?? 0) === 0
			? '0%'
			: equivalentActiveCount > 0
				? `${((equivalentActiveCount - 0.5) / days.length) * 100}%`
				: '0%';

	const [barHeight, setBarHeight] = useState(
		window.innerWidth < 768 ? 25 : 50
	);

	useEffect(() => { // auto lấy value đang được chọn khi có state = Opened
		const lastActiveIndex = days.reduce(
			(last, item, index) => (item.active ? index : last),
			-1
		);
		if (lastActiveIndex !== -1) {
			const item = days[lastActiveIndex];

			const icon =
				lastActiveIndex === 0
					? piece
					: imageMap[item.value]?.[
					item.active ? 'active' : 'inactive'
					] ?? piece;

			setValue(icon);
		}
	}, [data]);

	useEffect(() => {
		const handleResize = () => {
			setBarHeight(window.innerWidth < 768 ? 25 : 50);
		};
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!data) return;
		if (hasUserInteracted.current) return;

		let defaultIcon;

		if (vaults.length === 0) {
			// Không có vault nào -> icon piece
			defaultIcon = piece;
		} else if (targetVaultIndex !== -1) {
			// Có vault đang Available hoặc Locked -> đây chính là vault được đánh dấu (marker)
			const targetVault = vaults[targetVaultIndex];
			const isCompleted = targetVault.state === 'Completed';
			defaultIcon =
				imageMap[targetVault.piece_required]?.[
				isCompleted ? "active" : "inactive"
				] ?? piece;
			setSelectedState?.(targetVault.state);
		} else {
			// Không còn vault Available/Locked -> tất cả đã Completed, lấy vault cuối cùng
			const lastVault = vaults[vaults.length - 1];
			defaultIcon = imageMap[lastVault.piece_required]?.["active"] ?? piece;
			setSelectedState?.(lastVault.state);
		}

		setValue(defaultIcon);
	}, [data?.piece_balance, data?.vaults]);

	const pieceBalance = data?.piece_balance ?? 0;
	const vaults = data?.vaults ?? [];

	const targetVaultIndex = (() => {
		const availableIndex = vaults.findIndex((item) => item.state === 'Available');
		if (availableIndex !== -1) return availableIndex;
		return vaults.findIndex((item) => item.state === 'Locked');
	})();

	return (
		<div className="
										pb-[5px]
										sm:pb-[5px] md:pb-[5px] 
										px-1 sm:pt-1 md:pt-1 lg:px-7 xl:px-7
										pt-[10px] sm:pt-[10px] md:pt-[20px] lg:pt-[40px] xl:pt-[70px]
		">
			<div className="relative min-h-[95px] sm:min-h-[95px] md:min-h-[95px] lg:min-h-[120px] xl:min-h-[120px]  w-full">
				{/* Crossbar */}
				<div
					className="absolute left-0 top-6 md:top-5 flex w-full items-center"
					style={{ height: barHeight }}
				>
					<div
						className="flex-1"
						style={{
							height: barHeight,
							backgroundImage: `url(${crossbarCenterDayImg})`,
							backgroundRepeat: "no-repeat",
							backgroundSize: "100% 100%",
							backgroundPosition: "center",
						}}
					/>
					{/* Progress */}
					<div
						className="absolute left-3 
						top-[7px] sm:top-[7px] md:top-[13px] lg:top-[10px] xl:top-[10px] 
						overflow-hidden 
						rounded-full 
						h-[10px] sm:h-[12px] md:h-[20px] lg:h-[25px] xl:h-[25px]"
						style={{ width: progressWidth }}
					>
						<img
							src={lineCrossbarImg}
							className="h-full w-full"
						/>
					</div>
				</div>

				{/* Items */}
				<div className="flex w-full justify-between">
					{days.map((item, index) => {
						const icon =
							index === 0
								? piece
								: imageMap[item.value]?.[
								item.active ? "active" : "inactive"
								] ?? piece;

						const notReached = index !== 0 && item.state !== "Completed" && pieceBalance < item.value;

						return (
							<div
								onClick={() => {
									if (notReached) return;
									hasUserInteracted.current = true;
									setValue(icon);
									setSelectedState?.(index === 0 ? "Locked" : item.state);
								}}
								key={item.id}
								className="flex flex-col items-center z-10"
							>
								{targetVaultIndex !== -1 && index === targetVaultIndex + 1 && (
									<img
										src={image487Img}
										className="absolute 
										-top-9
										sm:-top-9 md:-top-9 lg:-top-20 xl:-top-20 
										w-auto 
										h-35
										sm:h-35
										md:h-35
										lg:h-60
										xl:h-60"
									/>
								)}
								{targetVaultIndex !== -1 && index === targetVaultIndex + 1 && (
									<img
										src={ellipseImg}
										className="absolute -top-[-12px] sm:-top-[-12px] md:-top-[-12px] lg:-top-1 xl:-top-1 h-3 sm:h-3 md:h-3 lg:h-5 xl:h-5 w-auto z-3 pl-10"
									/>
								)}

								<div className="h-16 flex items-center justify-center z-2">
									<img
										alt='icon'
										src={icon}
										className={`
										h-13
										sm:h-13
										md:h-25
										w-auto
										object-contain
										${notReached ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}
										`}
									/>
								</div>
								<span className="mt-1 sm:mt-1 md:mt-10 lg:mt-10 xl:mt-10 
								text-sm sm:text-sm md:text-xl lg:text-2xl xl:text-2xl font-bold text-[#7B5125]">
									{item?.value?.toLocaleString("en-US")}
								</span>
							</div>
						);
					})}
				</div>
			</div>
		</div>
	);
}